import { type FC, useEffect, useLayoutEffect, useRef } from "react";
import { translate } from "lib/blocs/TranslateionBloc";
import type { TourStepDefinition, TourPlacement } from "./types";
import { DEFAULT_PLACEMENT } from "./types";
import { VIEWPORT_SAFE_MARGIN_PX } from "./constants";
import {
  TooltipContainer,
  TooltipCard,
  TooltipStepCounter,
  TooltipText,
  TooltipFooter,
  TooltipButton
} from "./tourGuide.styles";

interface TourGuideTooltipProps {
  step: TourStepDefinition;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
}

const STEP_TEXT_ID = "tour-guide-step-text";

const SLIDE_ENTER: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: 12 },
  top: { y: -12 },
  left: { x: -12 },
  right: { x: 12 }
};

const SLIDE_EXIT: Record<TourPlacement, { x?: number; y?: number }> = {
  bottom: { y: 6 },
  top: { y: -6 },
  left: { x: -6 },
  right: { x: 6 }
};

const ENTER_TRANSITION = {
  type: "spring" as const,
  damping: 22,
  stiffness: 340,
  mass: 0.8
};
const EXIT_TRANSITION = { duration: 0.18, ease: [0.4, 0, 1, 1] as const };

function computeHorizontalLeft(
  anchorName: string,
  container: HTMLElement
): number | null {
  const anchor = document.querySelector<HTMLElement>(
    `[data-tour-anchor="${anchorName}"]`
  );
  if (!anchor) {
    return null;
  }

  const anchorRect = anchor.getBoundingClientRect();
  const anchorCenter = anchorRect.left + anchorRect.width / 2;
  const tooltipWidth = container.offsetWidth;
  const vw = window.innerWidth;

  let left = anchorCenter - tooltipWidth / 2;
  left = Math.max(VIEWPORT_SAFE_MARGIN_PX, left);
  left = Math.min(vw - VIEWPORT_SAFE_MARGIN_PX - tooltipWidth, left);
  return left;
}

const TourGuideTooltip: FC<TourGuideTooltipProps> = ({
  step,
  stepIndex,
  totalSteps,
  onNext
}) => {
  const placement = step.placement ?? DEFAULT_PLACEMENT;
  const isLast = stepIndex === totalSteps - 1;
  const buttonLabel = step.buttonTextKey
    ? translate(step.buttonTextKey)
    : isLast
      ? translate("tourGuide.gotIt")
      : translate("tourGuide.next");

  const cardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const isHorizontal = placement === "top" || placement === "bottom";
    if (!isHorizontal || !containerRef.current) {
      return;
    }

    const left = computeHorizontalLeft(step.anchorName, containerRef.current);
    if (left != null) {
      containerRef.current.style.left = `${left}px`;
    }
  }, [step.anchorName, placement, stepIndex]);

  useEffect(() => {
    cardRef.current?.focus();
  }, [stepIndex]);

  return (
    <TooltipContainer
      ref={containerRef}
      anchorName={step.anchorName}
      placement={placement}
      role="dialog"
      aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
      aria-describedby={STEP_TEXT_ID}
      initial={{ opacity: 0, ...SLIDE_ENTER[placement] }}
      animate={{ opacity: 1, x: 0, y: 0, transition: ENTER_TRANSITION }}
      exit={{
        opacity: 0,
        ...SLIDE_EXIT[placement],
        transition: EXIT_TRANSITION
      }}
    >
      <TooltipCard
        ref={cardRef}
        tabIndex={0}
        role="button"
        onClick={onNext}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onNext();
          }
        }}
      >
        <TooltipText id={STEP_TEXT_ID}>
          {translate(step.textKey, step.textVars)}
        </TooltipText>
        <TooltipFooter>
          <TooltipStepCounter aria-hidden="true">
            {stepIndex + 1}/{totalSteps}
          </TooltipStepCounter>
          <TooltipButton aria-hidden="true" tabIndex={-1}>
            {buttonLabel}
          </TooltipButton>
        </TooltipFooter>
      </TooltipCard>
    </TooltipContainer>
  );
};

export default TourGuideTooltip;
